import React, { useState, useEffect } from 'react';
import { FaComments, FaCheckCircle, FaTimesCircle, FaCalendarWeek, FaCalendarAlt } from 'react-icons/fa';
import { fetchChatLogDashboard, fetchChatLogAnalytics } from '../../services/service';
import { useAsgardeoAuth } from '../../hooks/useAsgardeoAuth';

// Reusable StatCard
const StatCard = ({ value, label, color, iconBg, icon }) => (
  <div className={`bg-pl_bg_light_3 rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 xl:p-8 border-t-4 ${color} flex flex-col items-center transition-transform duration-200 min-w-[120px] sm:min-w-[150px] lg:min-w-[180px] xl:min-w-[200px] group`}>
    <div className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 xl:w-16 xl:h-16 flex items-center justify-center rounded-full mb-1 sm:mb-2 shadow-md ${iconBg} transition-all duration-200`}>
      {icon}
    </div>
    <div className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-extrabold mb-1 text-pl_txt_light_1">{value}</div>
    <div className="text-xs sm:text-sm lg:text-base font-semibold text-pl_txt_light_2 text-center leading-tight">{label}</div>
  </div>
);

const SectionHeader = ({ children }) => (
  <div className="mb-3 sm:mb-4 lg:mb-6 mt-4 sm:mt-6 lg:mt-10">
    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-pl_prim_color_1 mb-1 sm:mb-2">{children}</h2>
    <div className="h-1 w-8 sm:w-12 lg:w-16 bg-pl_prim_color_2 rounded mb-1 sm:mb-2"></div>
  </div>
);

const ChatLogDashboard = () => {
  const { getAccessToken, getIDToken, getDecodedIDToken } = useAsgardeoAuth();
  const [dashboard, setDashboard] = useState({ todayCount: 0, todaySuccess: 0, failedByHour: Array(24).fill(0) });
  const [analytics, setAnalytics] = useState({ totalChats: 0, totalSuccess: 0, totalFailed: 0, perDay: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const authContext = { getAccessToken, getIDToken, getDecodedIDToken };
      const [dashboardData, analyticsData] = await Promise.all([
        fetchChatLogDashboard(authContext),
        fetchChatLogAnalytics(authContext)
      ]);
      setDashboard(dashboardData);
      setAnalytics(analyticsData);
      setLastUpdate(new Date());
      console.log('Dashboard updated:', new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      
      // Handle specific error types
      if (err.message.includes('Authentication required')) {
        setError('Please sign in again to view dashboard data.');
      } else if (err.message.includes('Access denied')) {
        setError('You do not have permission to view dashboard data.');
      } else if (err.message.includes('Server error')) {
        setError('Server is temporarily unavailable. Please try again later.');
      } else {
        setError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [getAccessToken, getIDToken, getDecodedIDToken]);

  const { todayCount, todaySuccess, failedByHour } = dashboard;
  const { totalChats, totalSuccess, totalFailed, perDay } = analytics;
  const maxFailed = Math.max(...failedByHour, 1);
  const maxPerDay = Math.max(...perDay.map(d => d.total), 1);

  // Calculate 7-day and 30-day counts
  const getLastNDaysCount = (days) => {
    if (!perDay || perDay.length === 0) return 0;
    
    const today = new Date();
    const cutoffDate = new Date(today);
    cutoffDate.setDate(today.getDate() - days);
    
    return perDay
      .filter(day => {
        const dayDate = new Date(day.date);
        return dayDate >= cutoffDate;
      })
      .reduce((sum, day) => sum + day.total, 0);
  };

  const last7DaysCount = getLastNDaysCount(7);
  const last30DaysCount = getLastNDaysCount(30);

  return (
    <div className="relative text-pl_txt_light_1">
      <div className="text-pl_txt_light_1 text-sm sm:text-base lg:text-xl py-2 sm:py-4 px-2 sm:px-4">
        {loading ? (
          <div className="text-center text-base sm:text-lg">Loading...</div>
        ) : error ? (
          <div className="text-red-500 text-center text-sm sm:text-base">{error}</div>
        ) : (
          <div className='grid gap-3 sm:gap-4 lg:gap-6 xl:gap-16 grid-cols-1 md:grid-cols-2'>
            {/* Today's stats */}
            <div className='bg-pl_bg_light_4 rounded-10 p-2 sm:p-3 lg:p-4'>
              <SectionHeader>Today's Overview</SectionHeader>
              <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-4 justify-items-center my-1">
            <StatCard 
                  value={todayCount} 
                label="Today's Chats"  
                color="border-blue-500" 
                  iconBg="bg-pl_bg_light_5" 
                  icon={<FaComments className="text-pl_prim_color_1 text-xl sm:text-2xl lg:text-3xl" />} 
              />
              <StatCard 
                  value={todaySuccess} 
                label="Today's Success" 
                color="border-green-500" 
                  iconBg="bg-pl_bg_light_5" 
                  icon={<FaCheckCircle className="text-green-600 text-xl sm:text-2xl lg:text-3xl" />} 
              />
            </div>
          </div>

            {/* Recent Period Stats */}
            <div className='bg-pl_bg_light_4 rounded-10 p-2 sm:p-3 lg:p-4'>
              <SectionHeader>Recent Period Overview</SectionHeader>
              <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-4 justify-items-center my-1">
              <StatCard 
                  value={last7DaysCount} 
                  label="Last 7 Days"  
                  color="border-purple-500"
                  iconBg="bg-pl_bg_light_5" 
                  icon={<FaCalendarWeek className="text-purple-600 text-xl sm:text-2xl lg:text-3xl" />} 
              />
              <StatCard 
                  value={last30DaysCount} 
                  label="Last 30 Days" 
                  color="border-indigo-500"
                  iconBg="bg-pl_bg_light_5" 
                  icon={<FaCalendarAlt className="text-indigo-600 text-xl sm:text-2xl lg:text-3xl" />} 
              />
            </div>
          </div>

            {/* All-time stats */}
            <div className='bg-pl_bg_light_4 rounded-10 p-2 sm:p-3 lg:p-4'>
              <SectionHeader>All-Time Overview</SectionHeader>
              <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4 justify-items-center">
              <StatCard 
                  value={totalChats} 
                  label="Total Chats" 
                  color="border-blue-500"
                  iconBg="bg-pl_bg_light_5" 
                  icon={<FaComments className="text-pl_prim_color_2 text-xl sm:text-2xl lg:text-3xl" />} 
              />
              <StatCard 
                  value={totalSuccess} 
                label="Total Success" 
                color="border-green-500" 
                  iconBg="bg-pl_bg_light_5" 
                  icon={<FaCheckCircle className="text-green-500 text-xl sm:text-2xl lg:text-3xl" />} 
              />
              <StatCard 
                  value={totalFailed} 
                label="Total Failed" 
                color="border-red-500" 
                  iconBg="bg-pl_bg_light_5" 
                  icon={<FaTimesCircle className="text-red-500 text-xl sm:text-2xl lg:text-3xl" />} 
              />
            </div>
          </div>

            {/* Per-day bar chart */}
            <div className='bg-pl_bg_light_4 rounded-10 p-2 sm:p-3 lg:p-4'>
              <SectionHeader>Chats Per Day (All Time)</SectionHeader>
            <div className="w-full overflow-x-auto flex justify-center items-end">
                <svg className="max-w-full h-24 sm:h-32 lg:h-44 min-w-[240px] sm:min-w-[280px] lg:min-w-[320px]" viewBox={`0 0 ${perDay.length * 30 + 40} 180`} preserveAspectRatio="none">
                  {perDay.map((d, i) => (
                  <g key={d.date}>
                    <rect
                      x={i * 30 + 30}
                        y={160 - (d.total / maxPerDay) * 120}
                      width={20}
                        height={(d.total / maxPerDay) * 120}
                      fill="#DF0D7D"
                      rx={5}
                      className="transition-all duration-200 hover:fill-pl_prim_color_2 cursor-pointer"
                    />
                      {/* Tooltip on hover */}
                    <title>{`${d.date}: ${d.total} chats`}</title>
                  </g>
                ))}
                  {/* Date labels */}
                  {perDay.map((d, i) => (
                  <text
                    key={d.date}
                    x={i * 30 + 40}
                    y={175}
                    fontSize={8}
                    textAnchor="end"
                    fill="#696969"
                    transform={`rotate(-45,${i * 30 + 40},175)`}
                  >
                    {d.date.slice(5)}
                  </text>
                ))}
              </svg>
            </div>
          </div>

            {/* Failed by hour chart */}
            <div className='bg-pl_bg_light_4 rounded-10 p-2 sm:p-3 lg:p-4'>
            <SectionHeader>Failed Chats by Hour (Today)</SectionHeader>
            <div className="w-full overflow-x-auto flex justify-center items-end">
                <svg className="w-full max-w-3xl h-20 sm:h-28 lg:h-36 min-w-[240px] sm:min-w-[280px] lg:min-w-[320px]" viewBox={`0 0 ${24 * 30 + 40} 140`} preserveAspectRatio="none">
                  {failedByHour.map((count, hour) => (
                  <g key={hour}>
                    <rect
                      x={hour * 30 + 30}
                        y={120 - (count / maxFailed) * 90}
                      width={20}
                        height={(count / maxFailed) * 90}
                      fill="#ef4444"
                      rx={5}
                      className="transition-all duration-200 hover:fill-red-400 cursor-pointer"
                    />
                      {/* Tooltip on hover */}
                    <title>{`Hour ${hour}: ${count} failed`}</title>
                  </g>
                ))}
                  {/* Hour labels */}
                  {failedByHour.map((_, hour) => (
                  <text
                    key={hour}
                    x={hour * 30 + 40}
                    y={135}
                    fontSize={8}
                    textAnchor="middle"
                    fill="#696969"
                  >
                    {hour}
                  </text>
                ))}
              </svg>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default ChatLogDashboard;